import { useContext } from 'react';
import { SearchLoadingAllContext, SearchResultsAllContext } from '@/containers/WorkspaceGlobalSearch/Context';
import type { WorkspaceSearchFull } from '@trinity/chat';
import { formatPlurality } from '@trinity/chat-components';
// import MemberSearchBanner from './MemberSearchBanner';
// import MembersTable from './Search.Table.Members';
import FilesTable from './Search.Table.Files';
import FoldersTable from './Search.Table.Folders';
import BrandsTable from './Search.Table.Brands';
import DashboardsTable from './Search.Table.Dashboards';
import QuantAnalysisTable from './Search.Table.QuantAnalyses';
import ProjectsTable from './Search.Table.Projects';
import TranscriptsTable from './Search.Table.Transcripts';
import styles from './style/Tab.module.css';

const pageSize = 10;

export default function SearchTabAll() {
  const loading = useContext(SearchLoadingAllContext);
  const { files, folders, brands, dashboards, quantanalyses, projects, transcripts } = useContext(SearchResultsAllContext);

  const isLoading = loading.untouched && loading.loading;

  return (
    <div className={styles.root}>
      <Brands
        result={brands}
        isLoading={isLoading} />
      <Projects
        result={projects}
        isLoading={isLoading} />
      <Transcripts
        result={transcripts}
        isLoading={isLoading} />
      <Files
        result={files}
        isLoading={isLoading} />
      <Folders
        result={folders}
        isLoading={isLoading} />
      <Dashboards
        result={dashboards}
        isLoading={isLoading} />
      <QuantAnalyses
        result={quantanalyses}
        isLoading={isLoading} />
    </div>
  );
}

function Files({ result, isLoading }: FilesProps) {
  const data = result?.items ?? [];
  const viewAll = result?.total > pageSize;
  const countText = !isLoading
    ? `(${getCountLabel(data.length, result?.total, 'File')})`
    : null;

  return (
    <FilesTable
      data={data}
      countText={countText}
      pageSize={pageSize}
      isLoading={isLoading}
      viewAll={viewAll} />
  );
}

function Transcripts({ result, isLoading }: TranscriptsProps) {
  const data = result?.items ?? [];
  const viewAll = result?.total > pageSize;
  const countText = !isLoading
    ? `(${getCountLabel(data.length, result?.total, 'Transcript')})`
    : null;

  return (
    <TranscriptsTable
      data={data}
      countText={countText}
      pageSize={pageSize}
      isLoading={isLoading}
      viewAll={viewAll} />
  );
}

function Folders({ result, isLoading }: FoldersProps) {
  const data = result?.items ?? [];
  const viewAll = result?.total > pageSize;
  const countText = !isLoading
    ? `(${getCountLabel(data.length, result?.total, 'Folder')})`
    : null;

  return (
    <FoldersTable
      data={data}
      countText={countText}
      pageSize={pageSize}
      isLoading={isLoading}
      viewAll={viewAll} />
  );
}

function Brands({ result, isLoading }: BrandsProps) {
  const data = result?.items ?? [];
  const viewAll = result?.total > pageSize;
  const countText = !isLoading
    ? `(${getCountLabel(data.length, result?.total, 'Brand')})`
    : null;

  return (
    <BrandsTable
      data={data}
      countText={countText}
      pageSize={pageSize}
      isLoading={isLoading}
      viewAll={viewAll} />
  );
}

function Projects({ result, isLoading }: ProjectsProps) {
  const data = result?.items ?? [];
  const viewAll = result?.total > pageSize;
  const countText = !isLoading
    ? `(${getCountLabel(data.length, result?.total, 'Project')})`
    : null;

  return (
    <ProjectsTable
      data={data}
      countText={countText}
      pageSize={pageSize}
      isLoading={isLoading}
      viewAll={viewAll} />
  );
}

function Dashboards({ result, isLoading }: DashboardsProps) {
  const data = result?.items ?? [];
  const viewAll = result?.total > pageSize;
  const countText = !isLoading
    ? `(${getCountLabel(data.length, result?.total, 'Dashboard')})`
    : null;

  return (
    <DashboardsTable
      data={data}
      countText={countText}
      pageSize={pageSize}
      isLoading={isLoading}
      viewAll={viewAll} />
  );
}

function QuantAnalyses({ result, isLoading }: QuantAnalysisProps) {
  const data = result?.items ?? [];
  const viewAll = result?.total > pageSize;
  // Adding Analyse instead of Analyses due to the plural method being forced.
  const countText = !isLoading
    ? `(${getCountLabel(data.length, result?.total, 'Analyse')})`
    : null;

  return (
    <QuantAnalysisTable
      data={data}
      countText={countText}
      pageSize={pageSize}
      isLoading={isLoading}
      viewAll={viewAll} />
  );
}

function getCountLabel(pageItems: number, total: number, label: string) {
  return total > pageItems
    ? `+${pageSize} ${formatPlurality(label, pageSize)}`
    : `${pageItems} ${formatPlurality(label, pageItems)}`;
}

export { SearchTabAll };

type FilesProps = {
  result: WorkspaceSearchFull.File.Results;
  isLoading: boolean;
};

type FoldersProps = {
  result: WorkspaceSearchFull.Folder.Results;
  isLoading: boolean;
};

type BrandsProps = {
  result: WorkspaceSearchFull.Brand.Results;
  isLoading: boolean;
}

type DashboardsProps = {
  result: WorkspaceSearchFull.Dashboard.Results;
  isLoading: boolean;
}

type QuantAnalysisProps = {
  result: WorkspaceSearchFull.QuantAnalyses.Results;
  isLoading: boolean;
}

type ProjectsProps = {
  result: WorkspaceSearchFull.Project.Results;
  isLoading: boolean;
}

type TranscriptsProps = {
  result: WorkspaceSearchFull.Transcript.Results;
  isLoading: boolean;
}